import { Component, ChangeDetectionStrategy, Input } from '@angular/core';

import { ViewData } from '../../model/view.data';
import { CellCheckedEvent, WizIcon, WizPopUp, WizRoot, WizEventManager } from '@wizeoo/akm-component-lib';
import { WIZ_BOOLEAN } from '@wizeoo/akm-core-lib';
import { Measure, WidgetConfigurationObj } from '@wizeoo/akm-message-lib';
import { BaselineEngine, FIELD_KIND, FeiKeyDef, FormulaCoreEngine, IBaselineValue, LoopResult, WizConstants } from '@wizeoo/akm-compute-lib';
import { WidgetLine } from '@wizeoo/akm-compute-lib';
import { Column } from 'ag-grid-community';
import { IForm } from '../../manager/widgets/i-form';
import { Pivot } from '../widgets/pivot/pivot.component';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { AlkEvent } from '../../framework/events/alk.event';
import { ALK_EVENT_TYPE, WIDGET_STATE } from '../../model/enums';
import { IWidget, PivotCellEditPopData } from '../../manager/widgets/i-widget';
import { WizColDef } from '../../manager/workingdata/fei.engine';
import { UpdateEntry, UpdateRequest } from '../../command/core/sync/sync.service';


@Component({
  selector: 'next-step-pivot-component',
  templateUrl: './next-step-pivot.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** pivot de sous-formulaire. Son FEI est un fake */
export class NextStepPivot extends Pivot {  
  
  // le véritable FEI manipulé, pour éventuel refresh des données sur modif externe
  private sourceFei: FeiVO; 
  // les modifications en cours, par WidgetLine
  public localUpdateMap: Map<WidgetLine, Map<Column, any>> = new Map();

  // formulaire source
  @Input() public parentWidget: IForm;

  public feedFromForm(sourceFei: FeiVO, fakeFei: FeiVO, context: WidgetConfigurationObj): void {
    this.sourceFei = sourceFei;
    this.fei = fakeFei;
    this.localUpdateMap = new Map();
    this.updateContext(context);
    this.state = WIDGET_STATE.FEI_COMPUTED;
    this.drawContent();

  }

  public get widget(): IWidget {
    return this.parentWidget;
  }


  ngAfterViewInit() {
    // pour interception d'éventuelle modif sur ma source réelle 
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.FEI_RECOMPUTED, this.recompute);
  }

  ngOnDestroy() {
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.FEI_RECOMPUTED);
  }

  public drawContent(): void {
    // console.log('nextStep fei', this.state);
    super.drawContent();
  }


  /**
   * altération du FEI sur retour de commit
   * update, create ou delete
   * @param e: AlkEvent, data = Map<EngineFeiVO, LoopResult> 
   * @override pour interception des modifs sur le vrai FEI
   */
  public recompute(e: AlkEvent): boolean {
    
    if(!this.sourceFei) {
      return;
    }

    let loopResults: Map<FeiVO, LoopResult> = e.data[0];  
    let result: boolean = false;

    if(loopResults.has(this.sourceFei)) { // je suis dedans
      result = true;
    } 
    else { // en cas d'héritage multiple, je suis peut-être impacté par la modif d'un enfant
      [...loopResults.keys()].forEach((fei: FeiVO) => {
        if(fei.visionStepLevel === this.sourceFei.visionStepLevel && this.sourceFei.requiredScenario.has(fei.scenario)) {
          result = true;
        }
      });   
    }

    if(result) { // je suis concerné
      setTimeout(() => {
        this.agGrid.api.refreshCells(); // refresh des values modifiées
      }, 0);  
    }
  }


  /**
   * override, pas de mémorisation du contexte, c'est un esclave
   * @param source les éléments de base pour la construction
   */
  public updateContext(source: WidgetConfigurationObj): void {
    this.context = source; // alimentation du contexte local
  }



  /**
   * méthode d'accès aux Keys / filterKeys du pivot
   * retourne la valeur de la cellule (pour les sorts, par exemple)
   * @param params la conf d'une cellule de grid. .data contient une WidgetLine
   * @Override pour injection des updates non persistés
   */
  public keyValueAccessor(params: any): any {
    
    // la ligne
    const line: WidgetLine = params.data; 
    // le code du champ. On split, il contient un level (CodeFD#level)
    const code: string = params.colDef.field.split(ViewData.KEY_SEPARATOR)[0];
    // le champ à utiliser sur le FDV
    let fdvfield: string = params.colDef.wcio.fdvf;
    // le pkd
    const fkd: FeiKeyDef = params.colDef.fkd;

    // le résultat. Pas de null ici, pour pouvoir comparer les chaines vides
    let result: string;
    
    if (fdvfield && fkd.isFreedata) { // je recherche un sous-champ sur un FDV (donc le code d'un autre FDV)
      result = BaselineEngine.getLabel(this.fei, line, code, fdvfield); // va retourne le libellé lié à la propriété fdvfield
    } else { // sinon on travaille avec la sortKey (pour tri). Elle est remplacée par la Key dans le keyFormatterAccessor
      result = BaselineEngine.getSort(this.fei, line, code, WizConstants.FIELD_LABEL); 
    }

    if(this.localUpdateMap.get(line)?.has(params.column)) {
      result = this.localUpdateMap.get(line).get(params.column);
    }

    if (!result || result === undefined) {
      result = ''; // neutralisation des null / undefined pour comparaison avec ''
    }
    return result;
  }

  /**
   * méthode d'accès générique sur les baselineValues.valor
   * @param params la conf d'une cellule de grid. .data contient un WidgetLine
   */
  public valueAccessor(params: any): any {
    
    let result: string = ''; // retour, pas de null
    
    const line: WidgetLine = params.data; // ligne source
    const colDef: WizColDef = params.colDef; // définition de la colonne
    const code: string = params.colDef.field; // champ mesure cible code#mode|fonction d'aggrégat
    const fkd: FeiKeyDef = params.colDef.fkd; // PKD

    // on le mode sur la colonne (params), en cas d'appel depuis une popUp (massEdit, massDelete)
    if (!colDef.widget.pivotMode) { // mode simple (GRID)
        // if(fkd.fullCode === 'LMA_ED1#I' && line.key === "PLAN2SCENAR|PLAN2SCENAR_102|KLE" ) {
        //   console.log('value cell exist ??',fkd.fullCode, line.key, line.cells?.get(fkd.fullCode));
        // }
      // si la cellule n'existe pas encore sur la baseline, création
      if(!line.cells?.get(fkd.fullCode)) {
        line.cells.set(fkd.fullCode, { k: line.key, m: fkd.fullCode, l: line.level, sc: line.scen });
      }

      result = BaselineEngine.getValue(this.fei, line, line.cells.get(fkd.fullCode)); 

      if(this.localUpdateMap.get(line)?.has(params.column)) {
        result = this.localUpdateMap.get(line).get(params.column);
      }

      // neutralisation du undefined sur les checkboxes : passage à false en mode simple
      if (colDef.datakind === FIELD_KIND.BOOLEAN) {
        if (!result || result === WIZ_BOOLEAN.UNKNOWN) {
          result = WIZ_BOOLEAN.FALSE;
        }
      }
    } 
    return result;
  }

  /** fin de traitement d'update, contrôle d'intégrité avant envoi
    * @param updateRequest porteuse des widgetLines sources
    * @param updates porteur des demandes de modifs, pour rollback éventuel 
    * @Override pour neutraliser le commit
    */
  public postControlBeforeCommit(updateRequest: UpdateRequest, updates: UpdateEntry[]): void {
    // console.log('nextStep postControlBeforeCommit', updates);
  }


  /**
   * callBack de retour de la popUp d'édition en masse
   * @param data PivotCellEditPopData, avec UpdateRequest
   */
  public backFromEditPop(data: PivotCellEditPopData): void {
    // la demande
    const updateRequest: UpdateRequest = data.updates;

    if (updateRequest?.lines?.length > 0) {
      updateRequest.lines.forEach((wl: WidgetLine) => { 
        this.interceptUpdate(wl, updateRequest.fkd, data.column, updateRequest.value);
      }); 
    }
  }

  /**
   * mise à jour de la valeur d'une cellule en édition directe
   * Donc 1 seule ligne, sinon il faut passer par la pop
   *
   * @param cell contient le node (la ligne), la WidgetLine porteuse, la colDef, la oldValue et la newValue
   * @override
   */
  public updateCell(cell: any): boolean {
    const wl : WidgetLine = cell.data;
    const wizColDef: WizColDef = cell.column.colDef as any as WizColDef;
    const fkd: FeiKeyDef = wizColDef.fkd;
    this.interceptUpdate(wl, fkd, cell.column, cell.newValue);
    return true;
  }

  /**
   * Handler sur le clic d'une colonne de type boolean (checkbox)
   * @param params tout le bazard { colDef, data, column, event, value }
   * @returns 
   */
  public onCellChecked(params: CellCheckedEvent): void {
    
    // la ligne base pour cette ligne. Ce n'est pas forcément la bonne ligne pour la colonne...
    const line: WidgetLine = params.data;
    const wizColDef: WizColDef = params.colDef as any as WizColDef; // définition de la colonne
    const fkd: FeiKeyDef = wizColDef.fkd;

    // forcer le passage en 2 états, le undefined est juste un état d'affichage en pivot
    if (params.newValue === WIZ_BOOLEAN.UNKNOWN) {
      params.newValue = WIZ_BOOLEAN.FALSE;
    }

    this.interceptUpdate(line, fkd, params.column, params.newValue);
  }


  /**
   * enregistrement des modifs en cours
   * @param wl 
   * @param fkd 
   * @param col 
   * @param value 
   */
  private interceptUpdate(wl: WidgetLine, fkd: FeiKeyDef, col: Column, value: any): void {
    if(!this.localUpdateMap.has(wl)) {
      this.localUpdateMap.set(wl, new Map());
    }

    this.localUpdateMap.get(wl).set(col, value);
    let update: UpdateEntry = this.model.dataEngine.generateUpdate(this.fei, wl, fkd, value); // pour alimentation de la BaselineValue
    if(fkd.isFilterKey) { // on force la mise à jour sur la baseline (pas fait par l'update)
      BaselineEngine.baselineSetFilterKeyValue(wl.bl, fkd.fullCode, value);
    }

    // mettre à jour la matrice
    let ibas: IBaselineValue = this.sourceFei.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, fkd.fullCode, value);
    // ajout de la dépendance sur la vrai mesure ??
    wl.cells.set(ibas.m, {l: wl.level, k: wl.key, sc: wl.scen, m: ibas.m});
    // console.log('interceptUpdate', ibas, wl.cells);
    FormulaCoreEngine.computeLines(this.fei, this.fei.getAllLines(), true);
    WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.FORM_SUB_LIST_UPDATE, this));
    setTimeout(() => {
      this.agGrid.api.refreshCells(); // refresh des values modifiées
    }, 0);
  }

  /** 
   * fonction de restriction d'édition
   * 
   * @param fei cible
   * @param meas mesure cible
   * @param wls pack de lignes
   * @param silentMode dispatch de l'erreur si false
   * @returns "Editable = TRUE" si la fonction editRestrict de la mesure retourne TRUE
   */
  public checkEditAvailable(fei: FeiVO, meas: Measure, wls: WidgetLine[], silentMode: boolean = false) : boolean { 
    
    if (!meas || !meas.editRestrict) {
      return true;
    }
    
    const allowed : boolean = this.model.widgetEngine.computeEditRestrict(fei, meas, wls, true, this.parentWidget.keyPartMap);
    if(!allowed && !silentMode) {
      // console.log('wb checkEdit', meas)
      let cleanMess: string = this.model.measureEngine.getMeasureLabel(meas);
      cleanMess = cleanMess  + '<br>';
      cleanMess += this.model.measureEngine.getMeasureEditRestrictMessage(meas);
      WizPopUp.toast(cleanMess, WizIcon.TOAST_ERROR).show();
    }
    return allowed;
  }

  /**
   * neutralisation de la filterFunc pour éviter la perte de résultat par filtre global sur FilterKey
   * @param item WidgetLine
   * @override
   */
  public mainFilterFunction(item: WidgetLine): boolean {
    return true;
  }


}
